import {
  Badge as ChakraBadge,
  Box,
  Flex,
  Modal as ChakraModal,
  ModalContent,
  ModalOverlay,
  Progress,
  Text,
  Tooltip,
  Icon
} from '@chakra-ui/react';
import {
  faArrowUpRightFromSquare,
  faArrowsRotate,
  faCircleCheck,
  faCircleInfo,
  faClock,
  faDownload,
  faExclamationTriangle,
  faEye,
  faFileLines,
  faHourglassHalf,
  faInfoCircle,
  faTriangleExclamation,
  faUserHeadset
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  ButtonIcon,
  ButtonText,
  Banner,
  Modal,
  OutlineCard,
  useToast,
} from '@morressier/alembic';
import { useEffect, useRef, useState } from 'react';
import {
  ISubmissionFile,
  ISubmissionFileSet
} from '@publishing-engine/domain/models';
import { getFiles } from '../fileSetSignature';
import {
  type ReplaceableFileField,
  useFileReplacementNavigation
} from '../fileReplacementNavigation';
import ContactUsProvider from 'src/pages/ContactUs/ContactUsProvider';
import { PdfViewerContainer } from 'src/components/PdfViewer/PdfViewerContainer';
import { downloadFile } from 'src/utils/file';

type Props = {
  fileSet: ISubmissionFileSet;
  hasFileChanges?: boolean;
  categoryLabels: Record<string, string>;
  onReplaceFile?: (field: ReplaceableFileField) => void;
  onGenerate: (rerun?: boolean) => void | Promise<void>;
  isGenerating: boolean;
  generationStatus: 'generating' | 'refreshing' | 'complete' | 'error' | null;
  generationError?: string | null;
  onCheckStatus: () => void;
  submissionContextId: string;
  organizationId?: string;
  isRequiredModalOpen: boolean;
  onCloseRequiredModal: () => void;
  isAcknowledgementRequiredModalOpen: boolean;
  onCloseAcknowledgementRequiredModal: () => void;
  onProceedToAcknowledgement: () => void;
  isAcknowledged: boolean;
  onAcknowledge: () => void;
  openPreviewForAcknowledgement: boolean;
  onPreviewOpenedForAcknowledgement: () => void;
};

const formatCategory = (category: string) =>
  category.replace(/([a-z])([A-Z])/g, '$1 $2');

const getReplacementStep = (
  category: string
): 'upload' | 'details' | 'preference' | 'author' => {
  if (category === 'Manuscript' || category === 'TitlePage') return 'details';
  if (
    category === 'LicensesAndPermissionsFiles' ||
    category === 'AnonymizedManuscript'
  )
    return 'preference';
  if (category === 'AuthorsCopyrightForm' || category === 'AuthorsICMJEForm') {
    return 'author';
  }
  return 'details';
};

const ConsolidatedSubmissionFiles: React.FC<Props> = ({
  fileSet,
  hasFileChanges = false,
  categoryLabels,
  onReplaceFile,
  onGenerate,
  isGenerating,
  generationStatus,
  generationError,
  onCheckStatus,
  submissionContextId,
  organizationId,
  isRequiredModalOpen,
  onCloseRequiredModal,
  isAcknowledgementRequiredModalOpen,
  onCloseAcknowledgementRequiredModal,
  onProceedToAcknowledgement,
  isAcknowledged,
  onAcknowledge,
  openPreviewForAcknowledgement,
  onPreviewOpenedForAcknowledgement
}) => {
  const files = getFiles(fileSet);
  const hasGeneratedPdfs = Boolean(
    fileSet.ConsolidatedPdf && fileSet.AnonymizedConsolidatedPdf
  );
  const [isProgressModalOpen, setIsProgressModalOpen] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const previewCloseButtonRef = useRef<HTMLButtonElement>(null);
  const [isContactSupportOpen, setIsContactSupportOpen] = useState(false);
  const returnToProgressAfterSupport = useRef(false);
  const toast = useToast();
  const navigateToFileReplacement = useFileReplacementNavigation();

  useEffect(() => {
    if (generationStatus === 'complete') {
      setIsProgressModalOpen(false);
      setIsPreviewModalOpen(true);
    }
  }, [generationStatus]);

  useEffect(() => {
    if (openPreviewForAcknowledgement) {
      setIsPreviewModalOpen(true);
      onPreviewOpenedForAcknowledgement();
    }
  }, [openPreviewForAcknowledgement, onPreviewOpenedForAcknowledgement]);

  const handleGenerate = (rerun = false) => {
    setIsProgressModalOpen(true);
    void onGenerate(rerun);
  };

  const handleDownloadPdf = (file?: ISubmissionFile) => {
    if (file) {
      void downloadFile(file.url, { submissionContextId, organizationId });
    }
  };

  const handleCheckStatus = () => {
    if (isGenerating) return;
    setIsProgressModalOpen(false);
    onCheckStatus();
  };

  const openContactSupport = (returnToProgress = false) => {
    returnToProgressAfterSupport.current = returnToProgress;
    setIsProgressModalOpen(false);
    setIsContactSupportOpen(true);
  };

  const handleContactSupportOpenChange = (isOpen: boolean) => {
    setIsContactSupportOpen(isOpen);
    if (!isOpen && returnToProgressAfterSupport.current) {
      setIsProgressModalOpen(true);
    }
    if (!isOpen) returnToProgressAfterSupport.current = false;
  };

  const handleAcknowledge = () => {
    onAcknowledge();
    setIsPreviewModalOpen(false);
    toast({
      variant: 'success',
      title: 'Consolidated PDFs generated, now you can submit your submission.',
      isClosable: true
    });
  };

  return (
    <Flex flexDirection="column" gap={2}>
      <Text textStyle="headingGroup" id="Consolidated Submission File">
        Consolidated Submission File
      </Text>
      <OutlineCard width="100%" padding={4}>
        {hasFileChanges && (
          <Box id="consolidated-file-changes-banner" mb={2}>
            <Banner
              variant="error"
              heading="Consolidated PDF Regeneration Required"
              content="You have replaced one or more submission files but have not regenerated the consolidated PDF. Please return to the Consolidated Submission File section and regenerate the PDF to ensure it reflects your latest files. You must regenerate the consolidated PDF before you can submit your manuscript."
            />
          </Box>
        )}
        <Flex flexDirection="column" gap={3}>
          <Flex
            alignItems={{ base: 'flex-start', md: 'center' }}
            justifyContent="space-between"
            gap={2}
            flexWrap="wrap"
          >
            <Text textStyle="textBody" flex={1} minWidth="220px" paddingEnd={5}>
              Generate consolidated PDFs from all uploaded files and acknowledge
              both anonymization variants before final submission.
            </Text>
            <Flex gap={2}>
              {[
                { label: 'Editorial View', available: isAcknowledged },
                {
                  label: 'Anonymous/ Reviewer View',
                  available: isAcknowledged
                }
              ].map(view => (
                <ChakraBadge
                  key={view.label}
                  py={2}
                  px={4}
                  color={view.available ? '#bef7b6' : '#D0D5DD'}
                  bg={view.available ? '#bef7b6' : '#D0D5DD'}
                  fontWeight="normal"
                >
                  <Text
                    textStyle="textBody"
                    textColor={view.available ? '#000000' : '#344054'}
                  >
                    <span style={{ marginRight: 5 }}>
                      <FontAwesomeIcon
                        icon={view.available ? faCircleCheck : faClock}
                      />{' '}
                    </span>
                    {view.label}
                  </Text>
                </ChakraBadge>
              ))}
            </Flex>
          </Flex>
          <Flex flexDirection="column" gap={1}>
            <Text textStyle="headingItem">Uploaded files ({files.length})</Text>
            <Text textStyle="textContext">
              If you encounter an issue with the consolidated PDFs, use the
              <strong> Replace</strong> option for the file you wish to
              substitute. This will take you to the relevant section in the
              process where you can replace the file, allowing you to regenerate
              the updated consolidated PDFs.
            </Text>
          </Flex>
          <Box borderTop="1px solid" borderColor="neutralPrimaryBorder">
            {files.length ? (
              files.map(({ category, file }, index) => (
                <Flex
                  key={`${file.id ?? file.url}-${index}`}
                  alignItems="center"
                  justifyContent="space-between"
                  gap={3}
                  py={2}
                  borderBottom="1px solid"
                  borderColor="neutralPrimaryBorder"
                >
                  <Flex minWidth={0} flexDirection="column" gap={2}>
                    <Text textStyle="headingItem">{file?.name}</Text>
                    <Flex alignItems="center" gap={1}>
                      <ChakraBadge
                        color={'#F2F4F7'}
                        bg={'#F2F4F7'}
                        borderRadius="full"
                        p={1}
                        px={2}
                      >
                        <Text textStyle="textContext" textColor="#344054">
                          {categoryLabels[category] || formatCategory(category)}
                        </Text>
                      </ChakraBadge>
                      {(category === 'TitlePage' ||
                        category === 'AdditionalSupplementary') && (
                        <Text textStyle="textContext">
                          <ButtonIcon
                            icon={<FontAwesomeIcon icon={faCircleInfo} />}
                            ariaLabel="Title page"
                          />
                          This file will not be included in the anonymous view.
                        </Text>
                      )}
                    </Flex>
                  </Flex>
                  <ButtonText
                    variant="primary"
                    rightIcon={
                      <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                    }
                    onClick={() => {
                      const field: ReplaceableFileField = {
                        key: category,
                        label:
                          categoryLabels[category] || formatCategory(category),
                        stepKey: getReplacementStep(category)
                      };
                      if (onReplaceFile) {
                        onReplaceFile(field);
                      } else {
                        navigateToFileReplacement(field);
                      }
                    }}
                  >
                    Replace
                  </ButtonText>
                </Flex>
              ))
            ) : (
              <Text textStyle="textContext" py={3} color="GrayText">
                No uploaded files
              </Text>
            )}
          </Box>
          <Flex justifyContent="flex-start" mt={4} gap={2} flexWrap="wrap">
            {hasGeneratedPdfs ? (
              <>
                <Button
                  variant="secondary"
                  rightIcon={<FontAwesomeIcon icon={faEye} />}
                  onClick={() => setIsPreviewModalOpen(true)}
                >
                  View PDFs
                </Button>
                <Button
                  variant="secondary"
                  rightIcon={<FontAwesomeIcon icon={faArrowsRotate} />}
                  onClick={() => handleGenerate(true)}
                  disabled={isGenerating}
                >
                  Re-generate Consolidated PDFs
                </Button>
              </>
            ) : (
              <Button
                variant="primary"
                rightIcon={<FontAwesomeIcon icon={faFileLines} />}
                onClick={() => handleGenerate()}
                disabled={isGenerating}
              >
                Generate Consolidated PDFs
              </Button>
            )}
          </Flex>
        </Flex>
      </OutlineCard>
      <Modal
        isOpen={isRequiredModalOpen}
        onClose={onCloseRequiredModal}
        width="590px"
        closeButtonProps={{ hidden: false }}
      >
        <Flex padding={8} flexDirection="column" gap={6}>
          <Flex alignItems="center" gap={2}>
            <FontAwesomeIcon
              icon={faTriangleExclamation}
              color="#D97706"
              fontSize="24px"
            />
            <Text textStyle="headingGroup">
              Consolidated Submission File Required
            </Text>
          </Flex>
          <Text textStyle="textBody">
            Please generate a merged PDF and acknowledge both the Editorial and
            Anonymous versions before submitting your manuscript.
          </Text>
          <Flex justifyContent="flex-end" gap={2}>
            <Button variant="secondary" onClick={onCloseRequiredModal}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                onCloseRequiredModal();
                handleGenerate(hasFileChanges);
              }}
            >
              Generate PDF
            </Button>
          </Flex>
        </Flex>
      </Modal>
      <Modal
        isOpen={isAcknowledgementRequiredModalOpen}
        onClose={onCloseAcknowledgementRequiredModal}
        width="590px"
        closeButtonProps={{ hidden: false }}
      >
        <Flex padding={8} flexDirection="column" gap={6}>
          <Flex alignItems="center" gap={2}>
            <FontAwesomeIcon
              icon={faTriangleExclamation}
              color="#D97706"
              fontSize="24px"
            />
            <Text textStyle="headingGroup">
              Consolidated PDFs Need Acknowledgment
            </Text>
          </Flex>
          <Text textStyle="textBody">
            Please review and acknowledge both the Editorial and Anonymous PDF
            versions before submitting your manuscript.
          </Text>
          <Flex justifyContent="flex-end" gap={2}>
            <Button
              variant="secondary"
              onClick={onCloseAcknowledgementRequiredModal}
            >
              Cancel
            </Button>
            <Button variant="primary" onClick={onProceedToAcknowledgement}>
              Proceed
            </Button>
          </Flex>
        </Flex>
      </Modal>
      <Modal
        isOpen={isProgressModalOpen}
        onClose={() => undefined}
        width="820px"
        closeOnEsc={false}
        closeOnOverlayClick={false}
        closeButtonProps={{ hidden: true }}
      >
        <Flex padding={8} flexDirection="column" gap={8}>
          <Flex
            width="100%"
            minHeight="56px"
            borderRadius="md"
            background={
              generationStatus === 'error'
                ? 'errorPrimaryFill'
                : 'neutralSecondaryFill'
            }
            alignItems="center"
            justifyContent="center"
            padding={8}
          >
            <FontAwesomeIcon
              icon={
                generationStatus === 'error'
                  ? faExclamationTriangle
                  : faHourglassHalf
              }
              fontSize="24px"
            />
          </Flex>
          <Box gap={4}>
            <Text textStyle="headingTitle" textAlign="center">
              {generationStatus === 'error'
                ? 'Unable to Generate Consolidated Submission Files'
                : 'Generating Consolidated Submission Files'}
            </Text>
            <Text textStyle="textContext" textAlign="center" mt={4}>
              {generationStatus === 'error'
                ? generationError ||
                  'We could not create consolidated PDFs from your uploaded files. Your files are still saved. Please try again. If the issue persists, contact customer support.'
                : `We are currently creating consolidated PDFs from all your uploaded
              files. This process might take some time. If you encounter any
              issues, feel free to reach out to customer support.`}
            </Text>
          </Box>
          <Box background="white" borderRadius="md" padding={2}>
            <Flex justifyContent="space-between" mb={2}>
              <Text textStyle="headingItem">Consolidated Submission Files</Text>
              <Text textStyle="textContext">
                {generationStatus === 'complete'
                  ? 'Complete'
                  : generationStatus === 'error'
                  ? 'Generation failed'
                  : 'Merging ...'}
              </Text>
            </Flex>
            <Progress
              size="sm"
              colorScheme={generationStatus === 'error' ? 'red' : 'blue'}
              isIndeterminate={isGenerating}
              value={
                generationStatus === 'complete' || generationStatus === 'error'
                  ? 100
                  : 0
              }
            />
          </Box>
          {generationStatus === 'error' && (
            <Flex justifyContent="space-between" gap={2}>
              <Button
                variant="caution"
                onClick={handleCheckStatus}
                disabled={isGenerating}
              >
                Exit
              </Button>
              <Flex justifyContent="flex-end" gap={3}>
                <Button
                  variant="secondary"
                  onClick={() => openContactSupport(true)}
                >
                  Contact Customer Support
                </Button>

                <Button
                  variant="primary"
                  onClick={() => handleGenerate(true)}
                  disabled={isGenerating}
                >
                  Retry
                </Button>
              </Flex>
            </Flex>
          )}
        </Flex>
      </Modal>
      <ChakraModal
        isOpen={isPreviewModalOpen}
        onClose={() => {
          if (!isContactSupportOpen) setIsPreviewModalOpen(false);
        }}
        closeOnEsc={!isContactSupportOpen}
        closeOnOverlayClick={!isContactSupportOpen}
        initialFocusRef={previewCloseButtonRef}
        size="full"
        isCentered
      >
        <ModalOverlay />
        <ModalContent
          width="100vw"
          height="100vh"
          maxWidth="100vw"
          maxHeight="100vh"
          margin={0}
          borderRadius={0}
          overflow="hidden"
          position="relative"
          background="surfaceSecondaryFill"
        >
          <Flex
            height="100%"
            minHeight={0}
            paddingX={4}
            flexDirection="column"
            gap={2}
          >
            <Box>
              <Box as="button" />
              <Flex justifyContent="space-between" alignItems="center">
                <Text textStyle="headingGroup">
                  Consolidated Submission File Preview
                  <Tooltip
                    hasArrow
                    shouldWrapChildren
                    placement="top"
                    label={
                      'If you find any issues in the preview, please replace the uploaded file in the submission flow - the preview will be updated on the next generation.'
                    }
                    borderRadius={4}
                  >
                    <ButtonIcon
                      size="small"
                      ml={1}
                      ariaLabel="Help Icon"
                      icon={<FontAwesomeIcon icon={faInfoCircle} />}
                    />
                  </Tooltip>
                </Text>
                <ButtonText
                  leftIcon={<Icon as={FontAwesomeIcon} icon={faUserHeadset} />}
                  onClick={() => {
                    setIsContactSupportOpen(true);
                  }}
                  mr={3}
                >
                  Contact Support
                </ButtonText>
              </Flex>
            </Box>

            <Flex
              flex={1}
              minHeight={0}
              gap={3}
              alignItems="stretch"
              flexDirection={{ base: 'column', md: 'row' }}
            >
              <Flex
                flex={1}
                minWidth={0}
                minHeight={0}
                flexDirection="column"
                gap={2}
              >
                <Flex justifyContent="space-between" alignItems="center">
                  <Text textStyle="headingItem">
                    Editorial View{' '}
                    <Tooltip
                      hasArrow
                      shouldWrapChildren
                      placement="top"
                      label={
                        'This version is for editors and administrators. It includes author names, affiliations, and other identifying details. Review it to confirm all information is complete and accurate.'
                      }
                      borderRadius={4}
                    >
                      <ButtonIcon
                        size="small"
                        ml={1}
                        ariaLabel="Help Icon"
                        icon={<FontAwesomeIcon icon={faInfoCircle} />}
                      />
                    </Tooltip>
                  </Text>
                  <Button
                    variant="secondary"
                    rightIcon={<FontAwesomeIcon icon={faDownload} />}
                    onClick={() => handleDownloadPdf(fileSet.ConsolidatedPdf)}
                  >
                    Download Editorial PDF
                  </Button>
                </Flex>
                {fileSet.ConsolidatedPdf ? (
                  <PdfViewerContainer
                    fileUrl={fileSet.ConsolidatedPdf.url}
                    fileExtension={fileSet.ConsolidatedPdf.extension}
                    submissionContextId={submissionContextId}
                    organizationId={organizationId}
                    viewerHeight="max(120px, calc(100vh - 245px))"
                  />
                ) : (
                  <Text textStyle="textContext">
                    Editorial PDF unavailable.
                  </Text>
                )}
              </Flex>
              <Flex
                flex={1}
                minWidth={0}
                minHeight={0}
                flexDirection="column"
                gap={2}
              >
                <Flex justifyContent="space-between" alignItems="center">
                  <Text textStyle="headingItem">
                    Anonymous View{' '}
                    <Tooltip
                      hasArrow
                      shouldWrapChildren
                      placement="top"
                      label={
                        'This version is shared with reviewers. Author names, affiliations, and other identifying information are removed to support blind peer review. Check that nothing identifying remains before you acknowledge.'
                      }
                      borderRadius={4}
                    >
                      <ButtonIcon
                        size="small"
                        ml={1}
                        ariaLabel="Help Icon"
                        icon={<FontAwesomeIcon icon={faInfoCircle} />}
                      />
                    </Tooltip>
                  </Text>
                  <Button
                    variant="secondary"
                    rightIcon={<FontAwesomeIcon icon={faDownload} />}
                    onClick={() =>
                      handleDownloadPdf(fileSet.AnonymizedConsolidatedPdf)
                    }
                  >
                    Download Anonymous PDF
                  </Button>
                </Flex>
                {fileSet.AnonymizedConsolidatedPdf ? (
                  <PdfViewerContainer
                    fileUrl={fileSet.AnonymizedConsolidatedPdf.url}
                    fileExtension={fileSet.AnonymizedConsolidatedPdf.extension}
                    submissionContextId={submissionContextId}
                    organizationId={organizationId}
                    viewerHeight="max(120px, calc(100vh - 245px))"
                  />
                ) : (
                  <Text textStyle="textContext">
                    Anonymous PDF unavailable.
                  </Text>
                )}
              </Flex>
            </Flex>
            <Flex
              position="absolute"
              bottom={0}
              left={0}
              right={0}
              justifyContent="space-between"
              alignItems="center"
              gap={3}
              flexWrap="wrap"
              flexShrink={0}
              paddingY={3}
              background="white"
              borderTopWidth="1px"
              borderTopColor="gray.200"
              zIndex={1}
              paddingX={4}
            >
              <Button
                variant="caution"
                onClick={() => setIsPreviewModalOpen(false)}
              >
                Exit
              </Button>
              <Flex gap={2} alignItems="center">
                <Text textStyle="textContext">
                  {!isAcknowledged
                    ? 'Please review and acknowledge each variation'
                    : 'Review the editorial and anonymous PDF previews.'}
                </Text>
                {!isAcknowledged && (
                  <Button
                    variant="primary"
                    rightIcon={<FontAwesomeIcon icon={faCircleCheck} />}
                    onClick={handleAcknowledge}
                  >
                    Acknowledge Consolidated PDFs
                  </Button>
                )}
              </Flex>
            </Flex>
          </Flex>
        </ModalContent>
      </ChakraModal>
      {isContactSupportOpen && (
        <ContactUsProvider
          isLoggedIn
          isOpen={isContactSupportOpen}
          setIsOpen={handleContactSupportOpenChange}
        />
      )}
    </Flex>
  );
};

export default ConsolidatedSubmissionFiles;
